import React, { useState, useEffect, useMemo, useCallback } from "react";
import { 
  DollarSign, 
  TrendingUp, 
  ShoppingBag, 
  Receipt, 
  RefreshCw,
  Clock,
  Calculator,
  CheckCircle2,
  Edit2,
  Plus,
  Trash2,
  Wand2,
  Check,
  X,
  User,
  BarChart3,
  Award,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from "recharts";
import { bomService } from "../../services/bomService";
import { suppliesService } from "../../services/suppliesService";
import { pb } from "../../services/pocketbase";
import { produtoDadosService } from "../../services/produtoDadosService";
import { getBaseSku, cleanSku, extractAroFromSku, isModeloComMedida, resolveModeloJoia } from "../../utils/skuUtils";
import { formatMoney } from "../../utils/formatters";

const CalculadoraAvulsaSubTab = React.lazy(() => import("../../tabs/CalculadoraAvulsaSubTab"));

/**
 * Helper para converter número ou string monetária em reais (float)
 */
function parseMoneyValue(val) {
  if (typeof val === "number" && !isNaN(val)) return val;
  if (typeof val === "string" && val.trim()) {
    const clean = val.replace("R$", "").replace(/\s/g, "");
    if (clean.includes(",")) {
      const normalized = clean.replace(/\./g, "").replace(",", ".");
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? 0 : parsed;
    }
    const parsed = parseFloat(clean);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

/**
 * Extrai número do aro (medida) a partir do SKU da variação ou do campo specs.size
 */
function resolveAroMedida(variationSku, rawSpecSize) {
  const fromSku = extractAroFromSku(variationSku);
  if (fromSku !== null) return String(fromSku);

  if (rawSpecSize !== undefined && rawSpecSize !== null && String(rawSpecSize).trim() !== "") {
    const str = String(rawSpecSize).trim();
    const fromSpec = extractAroFromSku(str);
    if (fromSpec !== null) return String(fromSpec);
    const numMatch = str.match(/(\d+(?:\.\d+)?)/);
    if (numMatch) {
      const val = parseFloat(numMatch[1]);
      if (!isNaN(val) && val >= 5 && val <= 42) return String(val);
    }
  }
  return null;
}

/**
 * Extrai o valor real de venda de um registro de pedido (production_orders / reservations)
 */
function extractOrderValueBRL(rec, matchedItem = null, fallbackUnitPrice = 0, qty = 1) {
  if (matchedItem) {
    const itemPrice = parseMoneyValue(
      matchedItem.unit_price ?? matchedItem.price ?? matchedItem.full_unit_price ?? matchedItem.valor_unitario
    );
    if (itemPrice > 0) return itemPrice * (Number(matchedItem.quantity || matchedItem.qty) || qty || 1);
  }

  let val = parseMoneyValue(rec.total_value ?? rec.price ?? rec.total_amount ?? rec.total);
  if (val > 0) return val;

  const fd = rec.full_data || {};
  const om = rec.order_metadata || fd.order_metadata || {};

  val = parseMoneyValue(
    om?.payment?.total ??
    fd.order?.payment?.total ??
    fd.payment?.total ??
    fd.total_value ??
    fd.total_amount ??
    fd.total_paid_amount ??
    fd.order?.total_amount ??
    fd.order?.total_paid_amount ??
    fd.price
  );
  if (val > 0) return val;

  const paymentsArr = Array.isArray(fd.payments)
    ? fd.payments
    : Array.isArray(fd.order?.payments)
    ? fd.order.payments
    : [];
  if (paymentsArr.length > 0) {
    const sum = paymentsArr.reduce((acc, p) => {
      return acc + parseMoneyValue(p.transaction_amount ?? p.total_paid_amount ?? p.amount ?? 0);
    }, 0);
    if (sum > 0) return sum;
  }

  const orderItemsArr = Array.isArray(fd.order_items)
    ? fd.order_items
    : Array.isArray(fd.order?.order_items)
    ? fd.order.order_items
    : [];
  if (orderItemsArr.length > 0) {
    const sum = orderItemsArr.reduce((acc, it) => {
      const p = parseMoneyValue(it.unit_price ?? it.full_unit_price ?? it.price ?? 0);
      const q = Number(it.quantity) || 1;
      return acc + p * q;
    }, 0);
    if (sum > 0) return sum;
  }

  if (fallbackUnitPrice > 0) {
    return fallbackUnitPrice * (qty || 1);
  }

  return 0;
}

const MONTH_NAMES_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/**
 * SecaoComercial.js — Seção Unificada Comercial, Precificação, Receita BOM (Modal),
 * Gráfico Mensal de Vendas, Ranking de Medidas (Anéis) e Histórico de Vendas.
 */
export default function SecaoComercial({
  skuPai,
  item,
  produtoDados: externalProdutoDados,
  activeTipoJoiaName,
  onProdutoDadosUpdated
}) {
  const [produtoDados, setProdutoDados] = useState(externalProdutoDados || null);
  const [localPriceOverride, setLocalPriceOverride] = useState(null);

  // Receita BOM e Insumos (carregados sob demanda no modal)
  const [recipe, setRecipe] = useState([]);
  const [recipeLoaded, setRecipeLoaded] = useState(false);
  const [loadingBom, setLoadingBom] = useState(false);
  const [suppliesList, setSuppliesList] = useState([]);
  const [suppliesLoaded, setSuppliesLoaded] = useState(false);

  // Modais
  const [isManualCostModalOpen, setIsManualCostModalOpen] = useState(false);
  const [isBomModalOpen, setIsBomModalOpen] = useState(false);
  const [isCalcModalOpen, setIsCalcModalOpen] = useState(false);

  // Form states p/ Editar Custo & Preço
  const [manualCostInput, setManualCostInput] = useState("");
  const [manualPriceInput, setManualPriceInput] = useState("");
  const [savingManualCost, setSavingManualCost] = useState(false);

  // Form states p/ Adicionar Insumo no Modal BOM
  const [selectedSupplyId, setSelectedSupplyId] = useState("");
  const [supplyQty, setSupplyQty] = useState("1");
  const [savingSupply, setSavingSupply] = useState(false);

  // Histórico Completo de Vendas & Controles de Visualização
  const [allSales, setAllSales] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [chartRangeMonths, setChartRangeMonths] = useState(12); // 6 | 12 | 0 (Tudo)
  const [showAllSalesRows, setShowAllSalesRows] = useState(false);

  // Sincroniza se o pai passar produtoDados atualizado
  useEffect(() => {
    if (externalProdutoDados !== undefined) {
      setProdutoDados(externalProdutoDados);
    }
  }, [externalProdutoDados]);

  // Reset states ao trocar de SKU
  useEffect(() => {
    setLocalPriceOverride(null);
    setRecipe([]);
    setRecipeLoaded(false);
    setShowAllSalesRows(false);
  }, [skuPai]);

  // Carrega produto_dados caso não tenha vindo do pai
  useEffect(() => {
    let active = true;
    const loadDados = async () => {
      if (!skuPai) return;
      try {
        const rec = await produtoDadosService.getProdutoDados(skuPai);
        if (active && rec) {
          setProdutoDados(rec);
        }
      } catch (err) {
        console.warn("Erro ao buscar produto_dados em SecaoComercial:", err);
      }
    };
    if (!externalProdutoDados) {
      loadDados();
    }
    return () => { active = false; };
  }, [skuPai, externalProdutoDados]);

  // Carrega receita BOM (chamado ao abrir modal BOM)
  const loadRecipe = useCallback(async () => {
    if (!skuPai) return [];
    setLoadingBom(true);
    try {
      const rec = await bomService.getRecipe(skuPai);
      setRecipe(rec || []);
      setRecipeLoaded(true);
      return rec || [];
    } catch (err) {
      console.error("Erro ao buscar receita BOM:", err);
      return [];
    } finally {
      setLoadingBom(false);
    }
  }, [skuPai]);

  // Carrega lista de insumos apenas quando o Modal de Receita BOM é aberto
  useEffect(() => {
    if (!isBomModalOpen) return;
    if (!recipeLoaded) {
      loadRecipe();
    }
    if (!suppliesLoaded) {
      suppliesService.getSupplies().then(sups => {
        setSuppliesList(sups || []);
        setSuppliesLoaded(true);
      }).catch(err => console.warn("Erro ao buscar insumos:", err));
    }
  }, [isBomModalOpen, recipeLoaded, suppliesLoaded, loadRecipe]);

  // Cálculo dos itens da Receita BOM (quando carregada)
  const recipeCalculated = useMemo(() => {
    if (!recipe || recipe.length === 0) return { items: [], directCostBRL: 0, contextualCostBRL: 0 };

    let directTotal = 0;
    let custoMetais = 0;
    let custoPedras = 0;
    let custoInsumos = 0;

    const items = recipe.map((row) => {
      const supply = row.expand?.supply_id || row.supply_id;
      const supplyName = supply?.name || row.supply_name || "Insumo Indefinido";
      const sector = supply?.sector || supply?.category || "Geral";
      const category = (supply?.category || "").toLowerCase();
      const sectorLower = (supply?.sector || "").toLowerCase();
      const nameLower = supplyName.toLowerCase();
      const unit = supply?.unidade_medida || supply?.unit || "un";
      const qty = Number(row.quantity_per_unit) || 0;
      const unitCost = suppliesService.getSupplyUnitCost(supply);
      const subtotal = qty * unitCost;
      directTotal += subtotal;

      const isMetal = category === "metal" || (sectorLower === "oficina" && (nameLower.includes("ouro") || nameLower.includes("prata")));
      const isStone = sectorLower === "pedras" || category.includes("zirconia") || category.includes("zircônia");

      if (isMetal) {
        custoMetais += (qty * unitCost * 4.0);
      } else if (isStone) {
        custoPedras += ((unitCost * qty * 2.0) + (2.0 * qty));
      } else {
        custoInsumos += (qty * unitCost * 2.0);
      }

      return {
        id: row.id,
        supplyId: supply?.id || row.supply_id,
        supplyName,
        sector,
        unit,
        qty,
        unitCost,
        subtotal
      };
    });

    const contextualTotal = Math.round((custoMetais + custoPedras + custoInsumos + 10.0) * 100) / 100;

    return {
      items,
      directCostBRL: Math.round(directTotal * 100) / 100,
      contextualCostBRL: contextualTotal
    };
  }, [recipe]);

  // Resolução do Preço de Venda Atual
  const precoVendaBRL = useMemo(() => {
    if (localPriceOverride !== null && localPriceOverride > 0) return localPriceOverride;
    const savedManualPrice = Number(
      produtoDados?.preco_venda_manual ??
      produtoDados?.ficha_tecnica?.preco_venda_manual ??
      0
    );
    if (savedManualPrice > 0) return savedManualPrice;
    return Number(item?.price || item?.full_data?.price || 0);
  }, [localPriceOverride, produtoDados, item]);

  // Resolução do Custo Atual
  const isCostManuallyOverridden = Boolean(
    produtoDados?.custo_editado_manualmente ||
    produtoDados?.ficha_tecnica?.custo_editado_manualmente
  );

  const custoSalvoCentavos = Number(
    produtoDados?.custo_bom_centavos ??
    produtoDados?.ficha_tecnica?.custo_bom_centavos ??
    0
  );

  const custoAtivoCentavos = useMemo(() => {
    if (custoSalvoCentavos > 0) return custoSalvoCentavos;
    if (recipeLoaded && recipeCalculated.directCostBRL > 0) {
      return Math.round(recipeCalculated.directCostBRL * 100);
    }
    return 0;
  }, [custoSalvoCentavos, recipeLoaded, recipeCalculated.directCostBRL]);

  const custoAtivoBRL = custoAtivoCentavos / 100;
  const precoVendaCentavos = Math.round(precoVendaBRL * 100);
  const margemBrutaCentavos = precoVendaCentavos - custoAtivoCentavos;
  const margemBrutaBRL = margemBrutaCentavos / 100;
  const margemPercentual = precoVendaCentavos > 0
    ? ((margemBrutaCentavos / precoVendaCentavos) * 100).toFixed(1)
    : "0.0";

  // Carrega Histórico Completo de Vendas do SKU Pai (para Gráfico Mensal + Ranking de Medidas + Tabela)
  useEffect(() => {
    let active = true;
    const loadSales = async () => {
      if (!skuPai) return;
      setLoadingSales(true);
      try {
        const salesMap = new Map();

        const addSaleEntry = (entry) => {
          const dedupKey = entry.orderNumber && entry.orderNumber !== "AVULSO" && !entry.orderNumber.startsWith("EST-")
            ? `${String(entry.orderNumber).trim().toUpperCase()}__${entry.variationSku}`
            : `${entry.id}__${entry.variationSku}`;

          if (!salesMap.has(dedupKey)) {
            salesMap.set(dedupKey, entry);
          } else {
            const existing = salesMap.get(dedupKey);
            if ((entry.price > 0 && existing.price === 0) || (entry.priority > existing.priority)) {
              salesMap.set(dedupKey, {
                ...existing,
                ...entry,
                price: entry.price > 0 ? entry.price : existing.price,
                aro: entry.aro || existing.aro
              });
            }
          }
        };

        const [prodOrdersRes, reservationsRes, soldStockRes, pdvSalesRes] = await Promise.all([
          // A. Todos os Pedidos de Produção (Ativos E Arquivados/Enviados) filtrados pelo SKU Pai
          pb.collection("production_orders").getList(1, 500, {
            sort: "-created",
            filter: `sku ~ "${skuPai}"`
          }).catch(() => ({ items: [] })),

          // B. Reservas de Pedidos filtradas pelo SKU Pai
          pb.collection("reservations").getList(1, 500, {
            sort: "-created",
            filter: `sku ~ "${skuPai}"`
          }).catch(() => ({ items: [] })),

          // C. Baixas de Venda Direta no Estoque Físico (status = "sold")
          pb.collection("inventory_items").getList(1, 500, {
            sort: "-updated",
            filter: `(sku ~ "${skuPai}" || baseSku ~ "${skuPai}") && status = "sold"`
          }).catch(() => ({ items: [] })),

          // D. Vendas PDV Showroom
          pb.collection("pdv_sales").getList(1, 200, {
            sort: "-created",
            filter: `items ~ "${skuPai}"`
          }).catch(() =>
            pb.collection("pdv_sales").getList(1, 100, { sort: "-created" }).catch(() => ({ items: [] }))
          )
        ]);

        // Processa A: production_orders
        (prodOrdersRes.items || []).forEach(rec => {
          const fullData = rec.full_data || {};
          const orderSku = cleanSku(rec.sku || fullData.sku || fullData.order_sku || "");
          const rawItems = Array.isArray(fullData.items) && fullData.items.length > 0
            ? fullData.items
            : Array.isArray(rec.items) && rec.items.length > 0
            ? rec.items
            : [];

          let matchedInOrder = false;
          let matchedSku = orderSku || skuPai;
          let matchedItem = null;

          if (orderSku && getBaseSku(orderSku) === skuPai) {
            matchedInOrder = true;
            matchedSku = orderSku;
          }

          if (rawItems.length > 0) {
            rawItems.forEach(it => {
              const itSku = cleanSku(it.sku || it.SKU || "");
              if (itSku && getBaseSku(itSku) === skuPai) {
                matchedInOrder = true;
                matchedSku = itSku;
                matchedItem = it;
              }
            });
          }

          if (!matchedInOrder) return;

          const specSize = rec.specs?.size || fullData.specs?.size || matchedItem?.size || matchedItem?.aro || "";
          const aroResolved = resolveAroMedida(matchedSku, specSize);
          const hasAroInSku = extractAroFromSku(matchedSku) !== null;
          const displayVariation = (!hasAroInSku && aroResolved)
            ? `${matchedSku} (Aro ${aroResolved})`
            : matchedSku;

          const qty = Number(matchedItem?.quantity || rec.quantity || fullData.quantity || 1);
          const orderNum = rec.order_number || fullData.order?.number || fullData.order_number || rec.id.substring(0, 8);
          const customerName = rec.customer_name || fullData.order?.customer?.name || fullData.customer?.name || fullData.linkedCustomer || "Cliente";
          const price = extractOrderValueBRL(rec, matchedItem, precoVendaBRL, qty);
          const date = rec.customCreatedAt || fullData.order?.date || fullData.created_at || rec.created;

          let statusLabel = rec.logistics_status || rec.status || fullData.status || "Em Produção";
          if (rec.archived) {
            statusLabel = (rec.logistics_status === "ENVIADO" || fullData.logisticsStatus === "ENVIADO")
              ? "Enviado"
              : "Concluído / Arquivado";
          }

          const isMl = fullData.source === "mercadolivre" || String(orderNum).startsWith("2000");
          const sourceLabel = isMl ? "Mercado Livre" : (rec.fromStock ? "Pronta Entrega" : "E-Commerce");

          addSaleEntry({
            id: `po_${rec.id}`,
            orderNumber: String(orderNum),
            customerName,
            source: sourceLabel,
            date,
            variationSku: displayVariation,
            aro: aroResolved,
            quantity: qty,
            price,
            status: statusLabel,
            priority: 3
          });
        });

        // Processa B: reservations
        (reservationsRes.items || []).forEach(rec => {
          const recSku = cleanSku(rec.sku || "");
          if (!recSku || getBaseSku(recSku) !== skuPai) return;

          const om = rec.order_metadata || {};
          const orderNum = om.number || rec.order?.number || `RES-${rec.id.substring(0, 6)}`;
          const customerName = rec.customer_name || om.customer?.name || "Cliente Reserva";
          const qty = Number(rec.quantity) || 1;
          const price = extractOrderValueBRL(rec, null, precoVendaBRL, qty);
          const date = rec.customCreatedAt || om.date || rec.created;
          const specSize = rec.specs?.size || om.specs?.size || "";
          const aroResolved = resolveAroMedida(recSku, specSize);
          const hasAroInSku = extractAroFromSku(recSku) !== null;
          const displayVariation = (!hasAroInSku && aroResolved)
            ? `${recSku} (Aro ${aroResolved})`
            : recSku;

          addSaleEntry({
            id: `res_${rec.id}`,
            orderNumber: String(orderNum),
            customerName,
            source: rec.source === "n8n" ? "E-Commerce (Reserva)" : "Reserva / Pedido",
            date,
            variationSku: displayVariation,
            aro: aroResolved,
            quantity: qty,
            price,
            status: rec.status || "Reservado",
            priority: 2
          });
        });

        // Processa C: pdv_sales (Showroom)
        (pdvSalesRes.items || []).forEach(rec => {
          const items = Array.isArray(rec.items) ? rec.items : (rec.full_data?.items || []);
          items.forEach((it, idx) => {
            const itSku = cleanSku(it.sku || "");
            if (!itSku || getBaseSku(itSku) !== skuPai) return;

            const qty = Number(it.quantity || 1);
            const itemPrice = (parseMoneyValue(it.price) * qty) || parseMoneyValue(rec.total || rec.total_amount) || (precoVendaBRL * qty);
            const orderNum = rec.order_number || rec.sale_number || `PDV-${rec.id.substring(0, 6)}`;
            const aroResolved = resolveAroMedida(itSku, it.size || it.aro || it.specs?.size);

            addSaleEntry({
              id: `pdv_${rec.id}_${idx}`,
              orderNumber: String(orderNum),
              customerName: rec.customer_name || "Cliente Showroom",
              source: "PDV Showroom",
              date: rec.created,
              variationSku: itSku,
              aro: aroResolved,
              quantity: qty,
              price: itemPrice,
              status: rec.status === "PDV_PENDENTE" ? "PDV Pendente" : "Venda PDV",
              priority: 3
            });
          });
        });

        // Processa D: inventory_items vendidos (baixas diretas de estoque)
        (soldStockRes.items || []).forEach(rec => {
          const recSku = cleanSku(rec.sku || rec.baseSku || "");
          if (!recSku || getBaseSku(recSku) !== skuPai) return;
          if (rec.soldBy === "sistema_interception") return;

          const aroResolved = resolveAroMedida(recSku, rec.specs?.size);

          addSaleEntry({
            id: `inv_${rec.id}`,
            orderNumber: `EST-${rec.id.substring(0, 6).toUpperCase()}`,
            customerName: rec.soldBy ? `Baixa por ${rec.soldBy}` : "Venda Estoque",
            source: "Estoque Físico",
            date: rec.dateOut || rec.updated || rec.created,
            variationSku: recSku,
            aro: aroResolved,
            quantity: 1,
            price: precoVendaBRL,
            status: "Vendido",
            priority: 1
          });
        });

        const finalSales = Array.from(salesMap.values()).sort(
          (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
        );

        if (active) {
          setAllSales(finalSales);
        }
      } catch (err) {
        console.error("Erro ao carregar histórico de vendas:", err);
      } finally {
        if (active) setLoadingSales(false);
      }
    };

    loadSales();
    return () => { active = false; };
  }, [skuPai, precoVendaBRL]);

  // Verifica se o produto é um Anel / Aliança (para exibir o Ranking de Medidas/Aros)
  const isAnelProduct = useMemo(() => {
    const tipoStr = activeTipoJoiaName || resolveModeloJoia(skuPai, item, produtoDados);
    if (isModeloComMedida(tipoStr)) return true;
    // Ou se houver vendas com aro identificado
    return allSales.some(s => s.aro !== null && s.aro !== undefined);
  }, [activeTipoJoiaName, skuPai, item, produtoDados, allSales]);

  // Dados agregados para o Gráfico de Vendas ao Longo dos Meses
  const monthlyChartData = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11

    // Mapa YYYY-MM -> { key, label, fullLabel, qtd, receita, pedidos }
    const monthBuckets = new Map();

    // Se chartRangeMonths > 0 (ex: 6 ou 12 meses), pré-cria todos os meses da janela para linha contínua
    if (chartRangeMonths > 0) {
      for (let i = chartRangeMonths - 1; i >= 0; i--) {
        const d = new Date(currentYear, currentMonth - i, 1);
        const y = d.getFullYear();
        const m = d.getMonth();
        const key = `${y}-${String(m + 1).padStart(2, "0")}`;
        const label = `${MONTH_NAMES_SHORT[m]}/${String(y).slice(-2)}`;
        monthBuckets.set(key, {
          key,
          label,
          qtd: 0,
          receita: 0,
          pedidos: 0
        });
      }
    }

    allSales.forEach(sale => {
      if (!sale.date) return;
      const d = new Date(sale.date);
      if (isNaN(d.getTime())) return;

      const y = d.getFullYear();
      const m = d.getMonth();
      const key = `${y}-${String(m + 1).padStart(2, "0")}`;
      const label = `${MONTH_NAMES_SHORT[m]}/${String(y).slice(-2)}`;

      if (chartRangeMonths > 0) {
        if (monthBuckets.has(key)) {
          const b = monthBuckets.get(key);
          b.qtd += Number(sale.quantity) || 1;
          b.receita += Number(sale.price) || 0;
          b.pedidos += 1;
        }
      } else {
        if (!monthBuckets.has(key)) {
          monthBuckets.set(key, { key, label, qtd: 0, receita: 0, pedidos: 0 });
        }
        const b = monthBuckets.get(key);
        b.qtd += Number(sale.quantity) || 1;
        b.receita += Number(sale.price) || 0;
        b.pedidos += 1;
      }
    });

    const sorted = Array.from(monthBuckets.values()).sort((a, b) => a.key.localeCompare(b.key));
    const totalPecas = sorted.reduce((acc, m) => acc + m.qtd, 0);
    const totalReceita = sorted.reduce((acc, m) => acc + m.receita, 0);
    const bestMonth = sorted.reduce((best, cur) => (cur.qtd > (best?.qtd || 0) ? cur : best), null);

    return {
      months: sorted,
      totalPecas,
      totalReceita,
      bestMonth
    };
  }, [allSales, chartRangeMonths]);

  // Ranking de Medidas (Aros) Mais Vendidas (para Anéis / Alianças)
  const aroRankingData = useMemo(() => {
    const countsByAro = new Map();
    let totalWithAro = 0;

    allSales.forEach(sale => {
      const aro = sale.aro;
      if (!aro) return;
      const qty = Number(sale.quantity) || 1;
      totalWithAro += qty;
      countsByAro.set(aro, (countsByAro.get(aro) || 0) + qty);
    });

    const list = Array.from(countsByAro.entries())
      .map(([aro, qtd]) => ({
        aro,
        label: `Aro ${aro}`,
        qtd,
        percent: totalWithAro > 0 ? Math.round((qtd / totalWithAro) * 1000) / 10 : 0
      }))
      .sort((a, b) => b.qtd - a.qtd || parseFloat(a.aro) - parseFloat(b.aro));

    const maxQtd = list.length > 0 ? list[0].qtd : 1;
    const top3Sum = list.slice(0, 3).reduce((acc, item) => acc + item.qtd, 0);
    const top3Share = totalWithAro > 0 ? Math.round((top3Sum / totalWithAro) * 100) : 0;

    return {
      list,
      totalWithAro,
      maxQtd,
      top3Share
    };
  }, [allSales]);

  const displayedSales = useMemo(() => {
    return showAllSalesRows ? allSales : allSales.slice(0, 15);
  }, [allSales, showAllSalesRows]);

  // Abrir Modal de Edição de Custo & Preço
  const handleOpenEditCostPrice = () => {
    setManualCostInput(custoAtivoBRL > 0 ? String(custoAtivoBRL.toFixed(2)) : "");
    setManualPriceInput(precoVendaBRL > 0 ? String(precoVendaBRL.toFixed(2)) : "");
    setIsManualCostModalOpen(true);
  };

  // Salvar Custo e Preço de Venda Editados Manualmente
  const handleSaveManualCost = async (e) => {
    e.preventDefault();
    if (!skuPai) return;

    const costVal = manualCostInput.trim() !== ""
      ? Number(String(manualCostInput).replace(",", "."))
      : custoAtivoBRL;
    const priceVal = manualPriceInput.trim() !== ""
      ? Number(String(manualPriceInput).replace(",", "."))
      : precoVendaBRL;

    if (isNaN(costVal) || costVal < 0) {
      return alert("Digite um valor de custo numérico válido.");
    }
    if (isNaN(priceVal) || priceVal < 0) {
      return alert("Digite um preço de venda numérico válido.");
    }

    setSavingManualCost(true);
    try {
      const costCentavos = Math.round(costVal * 100);
      const updated = await produtoDadosService.updatePrecoECusto(skuPai, {
        custoCentavos: costCentavos,
        precoVendaBRL: priceVal > 0 ? priceVal : undefined,
        isManualCost: true
      });

      if (updated) {
        setProdutoDados(updated);
        if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);
      }
      if (priceVal > 0) {
        setLocalPriceOverride(priceVal);
      }

      setIsManualCostModalOpen(false);
    } catch (err) {
      alert("Erro ao salvar custo e preço: " + err.message);
    } finally {
      setSavingManualCost(false);
    }
  };

  // Restaurar Cálculo Automático via BOM
  const handleResetAutoCost = async () => {
    if (!skuPai) return;
    setSavingManualCost(true);
    try {
      let currentRec = recipe;
      if (!recipeLoaded) {
        currentRec = await loadRecipe();
      }
      let autoCostBRL = 0;
      (currentRec || []).forEach(row => {
        const supply = row.expand?.supply_id || row.supply_id;
        const qty = Number(row.quantity_per_unit) || 0;
        const unitCost = suppliesService.getSupplyUnitCost(supply);
        autoCostBRL += qty * unitCost;
      });

      const autoCentavos = Math.round(autoCostBRL * 100);
      const updated = await produtoDadosService.updatePrecoECusto(skuPai, {
        custoCentavos: autoCentavos,
        precoVendaBRL: precoVendaBRL > 0 ? precoVendaBRL : undefined,
        isManualCost: false
      });

      if (updated) {
        setProdutoDados(updated);
        if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);
      }
    } catch (err) {
      alert("Erro ao restaurar cálculo automático: " + err.message);
    } finally {
      setSavingManualCost(false);
    }
  };

  // Adicionar Insumo à Receita BOM (dentro do Modal BOM)
  const handleAddSupplyToBom = async (e) => {
    e.preventDefault();
    if (!selectedSupplyId || !supplyQty || Number(supplyQty) <= 0) return;
    setSavingSupply(true);
    try {
      await bomService.addSupplyToRecipe(skuPai, selectedSupplyId, Number(supplyQty));
      const newRec = await loadRecipe();
      setSelectedSupplyId("");
      setSupplyQty("1");

      if (!isCostManuallyOverridden) {
        let sum = 0;
        newRec.forEach(r => {
          const sup = r.expand?.supply_id || r.supply_id;
          sum += (Number(r.quantity_per_unit) || 0) * suppliesService.getSupplyUnitCost(sup);
        });
        const updated = await produtoDadosService.updatePrecoECusto(skuPai, {
          custoCentavos: Math.round(sum * 100),
          isManualCost: false
        });
        if (updated) {
          setProdutoDados(updated);
          if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);
        }
      }
    } catch (err) {
      alert("Erro ao adicionar insumo ao BOM: " + err.message);
    } finally {
      setSavingSupply(false);
    }
  };

  // Remover Insumo do BOM (dentro do Modal BOM)
  const handleRemoveSupplyFromBom = async (bomId) => {
    if (!window.confirm("Remover este insumo da receita BOM?")) return;
    try {
      await bomService.removeSupplyFromRecipe(bomId);
      const newRec = await loadRecipe();
      if (!isCostManuallyOverridden) {
        let sum = 0;
        newRec.forEach(r => {
          const sup = r.expand?.supply_id || r.supply_id;
          sum += (Number(r.quantity_per_unit) || 0) * suppliesService.getSupplyUnitCost(sup);
        });
        const updated = await produtoDadosService.updatePrecoECusto(skuPai, {
          custoCentavos: Math.round(sum * 100),
          isManualCost: false
        });
        if (updated) {
          setProdutoDados(updated);
          if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);
        }
      }
    } catch (err) {
      alert("Erro ao remover insumo do BOM: " + err.message);
    }
  };

  // Gerar BOM a partir da Ficha Técnica (dentro do Modal BOM)
  const handleGenerateBomFromFichaTecnica = async () => {
    const ft = produtoDados?.ficha_tecnica;
    if (!ft || (!ft.peso_bruto_g && !ft.peso_gramas && !ft.peso_liquido_g)) {
      return alert("Preencha o peso e dados na Ficha Técnica primeiro para gerar a receita BOM automaticamente.");
    }

    if (!window.confirm("Deseja gerar/atualizar os insumos da receita BOM com base nos metais e pedras da Ficha Técnica?")) return;

    setLoadingBom(true);
    try {
      let currentSupplies = suppliesList;
      if (!suppliesLoaded || currentSupplies.length === 0) {
        currentSupplies = await suppliesService.getSupplies();
        setSuppliesList(currentSupplies || []);
        setSuppliesLoaded(true);
      }

      const pesoGrama = Number(ft.peso_bruto_g || ft.peso_gramas || ft.peso_liquido_g) || 0;
      const ligaNome = String(ft.liga_metal || "Prata 925").toLowerCase();

      const metalSupply = currentSupplies.find(s =>
        (s.category === "metal" || s.sector === "oficina") &&
        (s.name.toLowerCase().includes(ligaNome.includes("ouro") ? "ouro" : "prata"))
      ) || currentSupplies.find(s => s.category === "metal" || s.sector === "oficina") || currentSupplies[0];

      if (metalSupply && pesoGrama > 0) {
        const existingMetal = recipe.find(r => (r.supply_id || r.expand?.supply_id?.id) === metalSupply.id);
        if (existingMetal) {
          await bomService.updateSupplyInRecipe(existingMetal.id, pesoGrama);
        } else {
          await bomService.addSupplyToRecipe(skuPai, metalSupply.id, pesoGrama);
        }
      }

      if (Array.isArray(ft.detalhes_pedras) && ft.detalhes_pedras.length > 0) {
        for (const pedra of ft.detalhes_pedras) {
          const pedraQty = Number(pedra.qtd) || 1;
          const stoneSupply = currentSupplies.find(s =>
            s.sector === "pedras" || s.name.toLowerCase().includes("zircônia") || s.name.toLowerCase().includes("pedra")
          );
          if (stoneSupply) {
            await bomService.addSupplyToRecipe(skuPai, stoneSupply.id, pedraQty);
          }
        }
      }

      const newRec = await loadRecipe();
      let sum = 0;
      newRec.forEach(r => {
        const sup = r.expand?.supply_id || r.supply_id;
        sum += (Number(r.quantity_per_unit) || 0) * suppliesService.getSupplyUnitCost(sup);
      });

      const updated = await produtoDadosService.updatePrecoECusto(skuPai, {
        custoCentavos: Math.round(sum * 100),
        isManualCost: false
      });
      if (updated) {
        setProdutoDados(updated);
        if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);
      }

      alert("Receita BOM sincronizada com sucesso a partir da Ficha Técnica!");
    } catch (err) {
      alert("Erro ao sincronizar BOM: " + err.message);
    } finally {
      setLoadingBom(false);
    }
  };

  const dataCalculado = produtoDados?.custo_calculado_em
    ? new Date(produtoDados.custo_calculado_em).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    : "Não registrado";

  return (
    <div className="space-y-6">
      {/* GRID UNIFICADO DE CARDS COMERCIAIS & PRECIFICAÇÃO */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        
        {/* CARD 1: PREÇO DE VENDA */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col justify-between space-y-3 relative overflow-hidden">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-xs font-bold uppercase tracking-wider">Preço de Venda</span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleOpenEditCostPrice}
                  className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors cursor-pointer"
                  title="Editar Preço de Venda e Custo da Peça"
                >
                  <Edit2 size={12} />
                  <span>Editar Preço / Custo</span>
                </button>
                <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
                  <DollarSign size={16} />
                </div>
              </div>
            </div>

            <div className="text-2xl font-black text-slate-900 font-mono">
              {formatMoney(precoVendaBRL)}
            </div>
          </div>

          <div className="text-[11px] text-slate-500 font-medium flex items-center justify-between border-t border-slate-100 pt-2">
            <span>
              {(localPriceOverride || produtoDados?.preco_venda_manual || produtoDados?.ficha_tecnica?.preco_venda_manual)
                ? "Atualizado na Ficha & Catálogo"
                : "Fonte: Catálogo da Loja"}
            </span>
            <CheckCircle2 size={13} className="text-emerald-500" />
          </div>
        </div>

        {/* CARD 2: CUSTO DA PEÇA (BOM / MANUAL) + BOTÃO MODAL RECEITA BOM */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col justify-between space-y-3 relative overflow-hidden">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-slate-500 gap-2">
              <span className="text-xs font-bold uppercase tracking-wider">Custo da Peça</span>
              <div className="flex items-center gap-1.5">
                {isCostManuallyOverridden ? (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-200 rounded-md text-[10px] font-bold">
                    Manual
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md text-[10px] font-bold">
                    Via BOM
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleOpenEditCostPrice}
                  className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors cursor-pointer"
                  title="Editar Custo Manualmente"
                >
                  <Edit2 size={13} />
                </button>
              </div>
            </div>

            <div className="flex items-baseline justify-between gap-2">
              <div className="text-2xl font-black text-slate-900 font-mono">
                {formatMoney(custoAtivoBRL)}
              </div>
              {isCostManuallyOverridden && (
                <button
                  type="button"
                  onClick={handleResetAutoCost}
                  disabled={savingManualCost}
                  className="text-[11px] font-bold text-amber-700 hover:text-amber-900 underline cursor-pointer"
                  title="Restaurar cálculo automático pela Receita BOM"
                >
                  Restaurar Auto
                </button>
              )}
            </div>
          </div>

          <div className="text-[11px] text-slate-500 font-medium flex items-center justify-between border-t border-slate-100 pt-2 gap-2">
            <span className="flex items-center gap-1 text-slate-400 truncate" title={`Última atualização: ${dataCalculado}`}>
              <Clock size={12} className="shrink-0" />
              <span className="truncate">{dataCalculado}</span>
            </span>

            <button
              type="button"
              onClick={() => setIsBomModalOpen(true)}
              className="px-2.5 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[11px] font-bold flex items-center gap-1.5 transition-colors shrink-0 cursor-pointer shadow-xs"
            >
              <Receipt size={12} className="text-blue-400" />
              <span>Receita BOM</span>
            </button>
          </div>
        </div>

        {/* CARD 3: MARGEM BRUTA ESTIMADA + CALCULADORA AVULSA */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col justify-between space-y-3 relative overflow-hidden">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-xs font-bold uppercase tracking-wider">Margem Bruta Estimada</span>
              <div className={`p-1.5 rounded-lg ${
                Number(margemPercentual) >= 50 
                  ? "bg-emerald-50 text-emerald-600" 
                  : Number(margemPercentual) >= 20 
                  ? "bg-amber-50 text-amber-600" 
                  : "bg-red-50 text-red-600"
              }`}>
                <TrendingUp size={16} />
              </div>
            </div>

            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black text-slate-900 font-mono">
                {formatMoney(margemBrutaBRL)}
              </span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                Number(margemPercentual) >= 50 
                  ? "bg-emerald-100 text-emerald-700" 
                  : Number(margemPercentual) >= 20 
                  ? "bg-amber-100 text-amber-700" 
                  : "bg-red-100 text-red-700"
              }`}>
                {margemPercentual}%
              </span>
            </div>
          </div>

          <div className="text-[11px] text-slate-400 font-medium flex items-center justify-between border-t border-slate-100 pt-2 gap-2">
            <span className="truncate">Preço − Custo</span>
            <button
              type="button"
              onClick={() => setIsCalcModalOpen(true)}
              className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors shrink-0 cursor-pointer"
            >
              <Calculator size={12} />
              <span>Simular Preço</span>
            </button>
          </div>
        </div>

      </div>

      {/* PAINEL COMERCIAL DE VENDAS: GRÁFICO MENSAL + RANKING DE MEDIDAS (ANÉIS) + TABELA */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} className="text-blue-600" />
            <div>
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                 Inteligência & Histórico de Vendas (SKU Pai: {skuPai})
              </h3>
              <p className="text-[11px] text-slate-400">
                Evolução mensal de vendas, ranking de medidas mais vendidas e histórico detalhado de pedidos.
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full self-start sm:self-auto">
            {allSales.length} venda(s) registrada(s)
          </span>
        </div>

        {loadingSales ? (
          <div className="py-8 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
            <RefreshCw size={16} className="animate-spin text-blue-600" />
            <span>Processando histórico e estatísticas de vendas do SKU {skuPai}...</span>
          </div>
        ) : allSales.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-xs">
            Nenhuma venda ou pedido registrado para o SKU pai <strong className="font-mono text-slate-600">{skuPai}</strong>.
          </div>
        ) : (
          <div className="space-y-6">
            {/* LINHA DE INSIGHTS VISUAIS: GRÁFICO MENSAL + RANKING DE MEDIDAS (SE ANEL) */}
            <div className={`grid grid-cols-1 ${isAnelProduct ? "lg:grid-cols-12" : "lg:grid-cols-1"} gap-5`}>
              
              {/* COLUNA ESQUERDA: GRÁFICO AO LONGO DOS MESES */}
              <div className={`${isAnelProduct ? "lg:col-span-7" : ""} bg-slate-50/70 border border-slate-200 rounded-xl p-4 flex flex-col justify-between space-y-4`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 text-blue-700 rounded-lg">
                      <BarChart3 size={16} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                        Vendas ao Longo dos Meses
                      </h4>
                      <span className="text-[11px] text-slate-500">
                        Total no período: <strong>{monthlyChartData.totalPecas} un.</strong> ({formatMoney(monthlyChartData.totalReceita)})
                        {monthlyChartData.bestMonth && monthlyChartData.bestMonth.qtd > 0 ? (
                          <> · Pico: <strong>{monthlyChartData.bestMonth.label} ({monthlyChartData.bestMonth.qtd} un.)</strong></>
                        ) : null}
                      </span>
                    </div>
                  </div>

                  {/* SELETOR DE PERÍODO DO GRÁFICO */}
                  <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-0.5 self-start sm:self-auto">
                    {[
                      { val: 6, label: "6M" },
                      { val: 12, label: "12M" },
                      { val: 0, label: "Tudo" }
                    ].map(opt => (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setChartRangeMonths(opt.val)}
                        className={`px-2.5 py-1 rounded-md text-[10px] font-bold transition-colors cursor-pointer ${
                          chartRangeMonths === opt.val
                            ? "bg-slate-900 text-white"
                            : "text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ÁREA DO GRÁFICO RECHARTS */}
                <div className="w-full h-52 pt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyChartData.months} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="label" 
                        tick={{ fontSize: 10, fill: "#64748b", fontWeight: 600 }} 
                        axisLine={{ stroke: "#cbd5e1" }}
                        tickLine={false}
                      />
                      <YAxis 
                        allowDecimals={false} 
                        tick={{ fontSize: 10, fill: "#64748b", fontWeight: 600 }} 
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        cursor={{ fill: "#f1f5f9" }}
                        content={({ active, payload }) => {
                          if (!active || !payload || !payload.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg border border-slate-700 space-y-0.5">
                              <div className="font-bold text-amber-400">{d.label}</div>
                              <div>Peças vendidas: <strong>{d.qtd} un.</strong></div>
                              <div>Receita estimada: <strong>{formatMoney(d.receita)}</strong></div>
                            </div>
                          );
                        }}
                      />
                      <Bar 
                        dataKey="qtd" 
                        name="Unidades Vendidas" 
                        fill="#2563eb" 
                        radius={[6, 6, 0, 0]} 
                        maxBarSize={38}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* COLUNA DIREITA: RANKING DE MEDIDAS (AROS) MAIS VENDIDAS (EXIBIDO PARA ANÉIS) */}
              {isAnelProduct && (
                <div className="lg:col-span-5 bg-amber-50/40 border border-amber-200/80 rounded-xl p-4 flex flex-col justify-between space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-amber-200/60 pb-2.5">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-amber-500 text-white rounded-lg shadow-2xs">
                        <Award size={16} />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                          Ranking de Medidas (Aros) Mais Vendidas
                        </h4>
                        <span className="text-[11px] text-slate-500">
                          Baseado em <strong>{aroRankingData.totalWithAro} un.</strong> com aro identificado
                        </span>
                      </div>
                    </div>
                  </div>

                  {aroRankingData.list.length === 0 ? (
                    <div className="py-8 text-center text-xs text-slate-400 italic">
                      As vendas registradas deste anel não possuem numeração de aro detalhada no SKU ou especificações.
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                        {aroRankingData.list.map((itemAro, idx) => {
                          const barWidth = Math.max(8, Math.round((itemAro.qtd / aroRankingData.maxQtd) * 100));
                          const isTop1 = idx === 0;
                          const isTop2 = idx === 1;
                          const isTop3 = idx === 2;

                          return (
                            <div key={itemAro.aro} className="bg-white border border-slate-200/90 rounded-lg px-3 py-2 space-y-1 shadow-2xs">
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                  <span className={`w-5 h-5 rounded-full text-[10px] font-black flex items-center justify-center shrink-0 ${
                                    isTop1
                                      ? "bg-amber-500 text-white shadow-2xs"
                                      : isTop2
                                      ? "bg-slate-300 text-slate-800"
                                      : isTop3
                                      ? "bg-amber-700/80 text-white"
                                      : "bg-slate-100 text-slate-500"
                                  }`}>
                                    {idx + 1}º
                                  </span>
                                  <span className="font-mono font-bold text-slate-900">
                                    {itemAro.label}
                                  </span>
                                </div>

                                <div className="flex items-center gap-2">
                                  <span className="font-mono font-black text-slate-900">
                                    {itemAro.qtd} un.
                                  </span>
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 min-w-[42px] text-right">
                                    {itemAro.percent}%
                                  </span>
                                </div>
                              </div>

                              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    isTop1 ? "bg-amber-500" : isTop2 ? "bg-amber-400" : "bg-blue-500"
                                  }`}
                                  style={{ width: `${barWidth}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {aroRankingData.list.length >= 2 && (
                        <div className="pt-2 border-t border-amber-200/60 text-[11px] text-amber-950 flex items-center justify-between">
                          <span>
                            Top medidas: <strong>{aroRankingData.list.slice(0, 3).map(a => `Aro ${a.aro}`).join(", ")}</strong>
                          </span>
                          <span className="font-bold bg-amber-200/70 px-2 py-0.5 rounded text-[10px]">
                            {aroRankingData.top3Share}% da demanda
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

            </div>

            {/* TABELA DE VENDAS DETALHADAS */}
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-bold border-b border-slate-200">
                  <tr>
                    <th className="py-2.5 px-3">Pedido / Ref</th>
                    <th className="py-2.5 px-3">Cliente</th>
                    <th className="py-2.5 px-3">Canal</th>
                    <th className="py-2.5 px-3">Variação Vendida</th>
                    <th className="py-2.5 px-3 text-center">Qtd</th>
                    <th className="py-2.5 px-3 text-right">Valor</th>
                    <th className="py-2.5 px-3 text-center">Data</th>
                    <th className="py-2.5 px-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {displayedSales.map((sale) => (
                    <tr key={sale.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-2.5 px-3 font-mono font-bold text-slate-800 whitespace-nowrap">
                        #{sale.orderNumber}
                      </td>
                      <td className="py-2.5 px-3 font-medium text-slate-700 max-w-[180px] truncate" title={sale.customerName}>
                        <span className="flex items-center gap-1 truncate">
                          <User size={12} className="text-slate-400 shrink-0" />
                          <span className="truncate">{sale.customerName || "Cliente"}</span>
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-semibold text-slate-700 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          sale.source.includes("PDV")
                            ? "bg-purple-100 text-purple-800"
                            : sale.source.includes("Mercado Livre")
                            ? "bg-amber-100 text-amber-900"
                            : sale.source.includes("Estoque")
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-blue-100 text-blue-800"
                        }`}>
                          {sale.source}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-mono text-slate-700 font-medium whitespace-nowrap">
                        {sale.variationSku}
                      </td>
                      <td className="py-2.5 px-3 text-center font-bold text-slate-800">
                        {sale.quantity}
                      </td>
                      <td className="py-2.5 px-3 text-right font-bold text-slate-900 font-mono whitespace-nowrap">
                        {sale.price > 0 ? formatMoney(sale.price) : "—"}
                      </td>
                      <td className="py-2.5 px-3 text-center text-slate-500 font-medium whitespace-nowrap">
                        {sale.date ? new Date(sale.date).toLocaleDateString("pt-BR") : "-"}
                      </td>
                      <td className="py-2.5 px-3 text-center font-medium whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          sale.status.includes("Enviado") || sale.status.includes("Concluído") || sale.status === "Vendido"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-slate-100 text-slate-700"
                        }`}>
                          {sale.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {allSales.length > 15 && (
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={() => setShowAllSalesRows(prev => !prev)}
                  className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  {showAllSalesRows ? (
                    <>
                      <ChevronUp size={14} />
                      <span>Mostrar apenas as 15 mais recentes</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown size={14} />
                      <span>Ver todas as {allSales.length} vendas deste produto</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MODAL 1: EDITAR CUSTO & PREÇO DE VENDA */}
      {isManualCostModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit2 size={18} className="text-amber-400" />
                <h3 className="font-bold text-sm">Editar Preço de Venda & Custo ({skuPai})</h3>
              </div>
              <button onClick={() => setIsManualCostModalOpen(false)} className="text-slate-400 hover:text-white cursor-pointer">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveManualCost} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                  Preço de Venda do Produto (R$)
                </label>
                <input
                  type="text"
                  value={manualPriceInput}
                  onChange={(e) => setManualPriceInput(e.target.value)}
                  placeholder="Ex: 219,90"
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2.5 font-mono text-base font-bold text-emerald-700 outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Atualiza imediatamente o valor de venda da peça na ficha e sincroniza com o catálogo.
                </p>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                  Custo da Peça (R$)
                </label>
                <input
                  type="text"
                  value={manualCostInput}
                  onChange={(e) => setManualCostInput(e.target.value)}
                  placeholder="Ex: 35,00"
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2.5 font-mono text-base font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Define o custo manual da peça (fica protegido contra sobrescrita automática até clicar em "Restaurar Auto").
                </p>
              </div>

              <div className="pt-3 flex items-center justify-end gap-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsManualCostModalOpen(false)}
                  className="px-4 py-2 text-slate-600 font-semibold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingManualCost}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow flex items-center gap-1.5 cursor-pointer"
                >
                  {savingManualCost ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                  <span>Salvar Alterações</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: RECEITA BOM (INSUMOS DA MATRIZ) SOB DEMANDA */}
      {isBomModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <Receipt size={18} className="text-blue-400" />
                <div>
                  <h3 className="font-bold text-sm">Receita BOM — Insumos da Matriz ({skuPai})</h3>
                  <p className="text-[11px] text-slate-400">
                    Gerencie os metais, pedras e insumos que compõem o custo técnico deste SKU.
                  </p>
                </div>
              </div>
              <button onClick={() => setIsBomModalOpen(false)} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 cursor-pointer">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5 flex-1">
              {/* RESUMO E GERADOR AUTOMÁTICO */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-6">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Custo Direto (Insumos)</span>
                    <span className="text-lg font-black font-mono text-slate-900">
                      {formatMoney(recipeCalculated.directCostBRL)}
                    </span>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Custo c/ Multiplicadores</span>
                    <span className="text-lg font-black font-mono text-blue-700">
                      {formatMoney(recipeCalculated.contextualCostBRL)}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGenerateBomFromFichaTecnica}
                  disabled={loadingBom}
                  className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs cursor-pointer self-start sm:self-auto"
                >
                  <Wand2 size={14} />
                  <span>Gerar BOM via Ficha Técnica</span>
                </button>
              </div>

              {/* FORMULÁRIO RÁPIDO PARA ADICIONAR INSUMO */}
              <form onSubmit={handleAddSupplyToBom} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                  Adicionar Insumo / Metal / Pedra à Receita
                </span>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 text-xs">
                  <select
                    value={selectedSupplyId}
                    onChange={(e) => setSelectedSupplyId(e.target.value)}
                    className="flex-1 bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Selecione um insumo da lista...</option>
                    {suppliesList.map((sup) => (
                      <option key={sup.id} value={sup.id}>
                        {sup.name} ({sup.sector || sup.category || "Geral"}) — {formatMoney(suppliesService.getSupplyUnitCost(sup))}/{sup.unidade_medida || "un"}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    step="0.001"
                    min="0.001"
                    value={supplyQty}
                    onChange={(e) => setSupplyQty(e.target.value)}
                    placeholder="Qtd"
                    className="w-full sm:w-28 bg-slate-50 border border-slate-300 rounded-lg p-2 font-mono font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />

                  <button
                    type="submit"
                    disabled={savingSupply}
                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer"
                  >
                    {savingSupply ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} className="text-emerald-400" />}
                    <span>Adicionar</span>
                  </button>
                </div>
              </form>

              {/* TABELA DE INSUMOS DA RECEITA */}
              {loadingBom ? (
                <div className="py-8 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                  <RefreshCw size={16} className="animate-spin text-blue-600" />
                  <span>Carregando receita BOM...</span>
                </div>
              ) : recipeCalculated.items.length === 0 ? (
                <div className="py-8 text-center text-slate-400 space-y-1 bg-slate-50 rounded-xl border border-slate-100">
                  <Receipt size={28} className="mx-auto stroke-1 opacity-40" />
                  <p className="text-xs font-medium text-slate-500">Nenhum insumo cadastrado na receita deste SKU.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                  <table className="w-full text-left text-xs text-slate-600">
                    <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-bold border-b border-slate-200">
                      <tr>
                        <th className="py-2.5 px-3">Insumo</th>
                        <th className="py-2.5 px-3">Setor</th>
                        <th className="py-2.5 px-3 text-center">Qtd / Un.</th>
                        <th className="py-2.5 px-3 text-right">Custo Unit.</th>
                        <th className="py-2.5 px-3 text-right">Subtotal</th>
                        <th className="py-2.5 px-3 text-center">Ação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {recipeCalculated.items.map((bom) => (
                        <tr key={bom.id} className="hover:bg-slate-50 transition-colors">
                          <td className="py-2.5 px-3 font-bold text-slate-800">{bom.supplyName}</td>
                          <td className="py-2.5 px-3 text-slate-500 uppercase text-[10px] font-semibold">{bom.sector}</td>
                          <td className="py-2.5 px-3 text-center font-mono font-semibold text-slate-700">
                            {bom.qty} {bom.unit}
                          </td>
                          <td className="py-2.5 px-3 text-right text-slate-600 font-mono">
                            {formatMoney(bom.unitCost)}
                          </td>
                          <td className="py-2.5 px-3 text-right font-bold text-slate-900 font-mono">
                            {formatMoney(bom.subtotal)}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveSupplyFromBom(bom.id)}
                              className="p-1 text-slate-400 hover:text-red-600 rounded transition-colors cursor-pointer"
                              title="Remover insumo"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-slate-50 border-t border-slate-200 px-6 py-3 flex items-center justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setIsBomModalOpen(false)}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold cursor-pointer"
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: CALCULADORA AVULSA STANDALONE */}
      {isCalcModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-5xl h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Calculator size={20} className="text-amber-400" />
                <h3 className="font-bold text-base">Calculadora de Precificação & Simulação (SKU: {skuPai})</h3>
              </div>
              <button onClick={() => setIsCalcModalOpen(false)} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 cursor-pointer">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
              <React.Suspense fallback={
                <div className="p-12 text-center text-slate-400 font-bold text-xs animate-pulse">
                  Carregando Calculadora Avulsa...
                </div>
              }>
                <CalculadoraAvulsaSubTab />
              </React.Suspense>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
